// Auth context for the partner portal.
//
// Session-cookie based: we trust the browser to hold the cookie, and use
// `/auth/me/` as the source of truth on app boot. A 401 from any
// /v1/* endpoint inside a query/mutation hook lets the caller know to bounce
// back to /signin — we don't intercept globally here because some pages want
// to show a real error UI instead of redirecting.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { ApiError, api } from "./api";
import type { MeResponse } from "./types";

interface AuthContextValue {
  me: MeResponse | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (identifier: string, password: string) => Promise<MeResponse>;
  signOut: () => Promise<void>;
  // Force a refetch (e.g. after a wallet topup so balance updates).
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const ME_KEY = ["auth", "me"] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  // Prime the CSRF cookie once on mount. Idempotent so it's fine to call
  // unconditionally — Django re-issues the same token if one already exists.
  useEffect(() => {
    api.primeCsrf().catch(() => {
      // CSRF priming is best-effort; sign-in will surface a real error if it matters.
    });
  }, []);

  const meQuery = useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await api.get<MeResponse>("/auth/me/");
      } catch (e) {
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
          return null;
        }
        throw e;
      }
    },
    staleTime: 30_000,
    retry: false,
  });

  const signInMutation = useMutation({
    mutationFn: async ({ identifier, password }: { identifier: string; password: string }) => {
      const payload: Record<string, string> = { password };
      if (identifier.includes("@")) payload.email = identifier;
      else payload.username = identifier;
      return api.post<MeResponse>("/auth/login/", payload);
    },
    onSuccess: (data) => {
      qc.setQueryData(ME_KEY, data);
    },
  });

  const signOutMutation = useMutation({
    mutationFn: async () => {
      await api.post<void>("/auth/logout/");
    },
    onSuccess: () => {
      qc.setQueryData(ME_KEY, null);
      qc.clear();
    },
  });

  const value = useMemo<AuthContextValue>(
    () => ({
      me: meQuery.data ?? null,
      isLoading: meQuery.isLoading,
      isAuthenticated: !!meQuery.data?.partner,
      signIn: async (identifier, password) =>
        signInMutation.mutateAsync({ identifier, password }),
      signOut: async () => {
        await signOutMutation.mutateAsync();
      },
      refresh: async () => {
        await qc.invalidateQueries({ queryKey: ME_KEY });
      },
    }),
    [meQuery.data, meQuery.isLoading, qc, signInMutation, signOutMutation],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
